import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../../../../components/ui/InputField';
import SelectField from '../../../../components/ui/SelectField';
import SegmentedTabs from '../../../../components/SegmentedTabs';
import AssetDetailSection from '../../../assets/components/AssetDetailSection';
import OpeningHoursSection from '../../../assets/components/OpeningHoursSection';
import CoordinatesFields from '../../../../components/location/CoordinatesFields';
import { useGetLocationListQuery } from '../../../../api/locations/locationsApiSlice';
import type { StationCreationForm, WizardVariant } from '../stationCreationForm';

interface LocationStepProps {
    /** A house reads "where the house stands" — same step, its own words. */
    variant?: WizardVariant;
    form: StationCreationForm;
    update: <K extends keyof StationCreationForm>(key: K, value: StationCreationForm[K]) => void;
}

/**
 * Step 1 — the Location (retained geographic entity, architecture §4). A station
 * is placed at an existing Location or a new one is created. The existing-location
 * picker reads the live location list (tenant-scoped).
 */
const LocationStep = ({ form, update, variant = 'station' }: LocationStepProps) => {
    const { t } = useTranslation();
    const { data: locations, isLoading } = useGetLocationListQuery(undefined);

    const { ids, labels } = useMemo(() => {
        const list = locations ?? [];
        return {
            ids: list.map((l) => l.id),
            labels: Object.fromEntries(list.map((l) => [l.id, l.name])) as Record<string, string>,
        };
    }, [locations]);

    const hasLocations = ids.length > 0;

    /**
     * Zero opening-hours rows means closed on all seven days, so an existing
     * location in that state cannot take a booking either. Derived, not stored
     * in an effect — one source of truth per piece of state (CLAUDE.md).
     */
    const selectedLocationNeedsHours = useMemo(() => {
        if (form.locationMode !== 'existing' || !form.existingLocationId) return false;
        const selected = (locations ?? []).find((l) => l.id === form.existingLocationId);
        return !!selected && (selected.openingHours ?? []).length === 0;
    }, [locations, form.locationMode, form.existingLocationId]);

    return (
        <AssetDetailSection
            title={t('station_create.location.title')}
            subtitle={variant === 'house'
                ? t('house_create.location.subtitle')
                : t('station_create.location.subtitle')}
        >
            <div className={'flex flex-col gap-5'}>
                <SegmentedTabs<'existing' | 'new'>
                    items={[
                        { key: 'new', label: t('station_create.location.mode_new') },
                        { key: 'existing', label: t('station_create.location.mode_existing') },
                    ]}
                    value={form.locationMode}
                    onChange={(key) => update('locationMode', key)}
                />

                {form.locationMode === 'existing' ? (
                    <SelectField
                        labelAbove
                        label={t('station_create.location.select_label')}
                        placeholder={t('station_create.location.select_placeholder')}
                        loading={isLoading}
                        options={ids}
                        optionLabels={labels}
                        noOptions={t('station_create.location.none')}
                        value={form.existingLocationId}
                        onChange={(v) => update('existingLocationId', v)}
                    />
                ) : (
                    <>
                        <InputField
                            labelAbove
                            label={t('station_create.location.new_name_label')}
                            placeholder={t('station_create.location.new_name_placeholder')}
                            value={form.newLocationName}
                            onChange={(v) => update('newLocationName', v)}
                        />
                        <p className={'text-sm text-foreground/60'}>
                            {variant === 'house'
                                ? t('house_create.location.name_is_house_hint')
                                : t('station_create.location.name_is_station_hint')}
                        </p>

                        <div className={'grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-4'}>
                            <InputField
                                labelAbove
                                label={t('station_create.location.street_label')}
                                placeholder={t('station_create.location.street_placeholder')}
                                value={form.newLocationStreet}
                                onChange={(v) => update('newLocationStreet', v)}
                            />
                            <InputField
                                labelAbove
                                label={t('station_create.location.house_number_label')}
                                placeholder={t('station_create.location.house_number_placeholder')}
                                value={form.newLocationHouseNumber}
                                onChange={(v) => update('newLocationHouseNumber', v)}
                            />
                        </div>
                        <div className={'grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-4'}>
                            <InputField
                                labelAbove
                                label={t('station_create.location.postal_code_label')}
                                placeholder={t('station_create.location.postal_code_placeholder')}
                                value={form.newLocationPostalCode}
                                onChange={(v) => update('newLocationPostalCode', v)}
                            />
                            <InputField
                                labelAbove
                                label={t('station_create.location.city_label')}
                                placeholder={t('station_create.location.city_placeholder')}
                                value={form.newLocationCity}
                                onChange={(v) => update('newLocationCity', v)}
                            />
                        </div>
                        <InputField
                            labelAbove
                            label={t('asset_detail.fields.district')}
                            help={t('station_detail.location.district_help')}
                            value={form.newLocationDistrict}
                            onChange={(v) => update('newLocationDistrict', v)}
                        />

                        <CoordinatesFields
                            value={{ geoLat: form.newLocationGeoLat, geoLng: form.newLocationGeoLng }}
                            onChange={(next) => {
                                update('newLocationGeoLat', next.geoLat);
                                update('newLocationGeoLng', next.geoLng);
                            }}
                            addressParts={[
                                form.newLocationStreet, form.newLocationHouseNumber,
                                form.newLocationPostalCode, form.newLocationCity,
                            ]}
                        />

                        {/* Opening hours belong to the LOCATION, so they are
                            asked only when one is created here. An existing
                            location keeps the hours it already has. They are
                            not optional in effect: a weekday with no row is
                            closed, so a station created without them is
                            bookable on no day of the week. */}
                        <OpeningHoursSection
                            openingHours={form.newLocationOpeningHours}
                            onChange={(hours) => update('newLocationOpeningHours', hours)}
                            onValidityChange={(valid) =>
                                update('newLocationOpeningHoursValid', valid)}
                        />
                    </>
                )}

                {form.locationMode === 'existing' && !isLoading && !hasLocations && (
                    <p className={'text-sm text-foreground/60'}>
                        {t('station_create.location.none_hint')}
                    </p>
                )}

                {/* An existing location with NO opening hours at all is closed
                    on every weekday — the same defect as a new one created
                    without them, and locations made by earlier versions of this
                    wizard are all in that state. Ask for them here; the backend
                    writes them only when the location has none, never over
                    hours somebody already set. */}
                {selectedLocationNeedsHours && (
                    <>
                        <p className={'text-sm text-foreground/60'}>
                            {t('station_create.location.existing_without_hours_hint')}
                        </p>
                        <OpeningHoursSection
                            openingHours={form.newLocationOpeningHours}
                            onChange={(hours) => update('newLocationOpeningHours', hours)}
                            onValidityChange={(valid) =>
                                update('newLocationOpeningHoursValid', valid)}
                        />
                    </>
                )}
            </div>
        </AssetDetailSection>
    );
};

export default LocationStep;
